import { jsxs, jsx } from "react/jsx-runtime";
const HEAVY_MODELS = ["gpt-5.2", "gpt-4o", "gpt-4o-mini", "gpt-4.1-mini"];
const LIGHT_MODELS = ["gpt-4o-mini", "gpt-4.1-mini", "gpt-4o", "gpt-5.2"];
const RECAT_MODELS = ["", "gpt-4o-mini", "gpt-4.1-mini", "gpt-4o", "gpt-5.2"];
const ANALYZE_PLAN_MODELS = ["", "gpt-4o-mini", "gpt-4.1-mini", "gpt-4o", "gpt-5.2"];
const TogglePair = ({ label, value, onLabel, offLabel, onToggle, disabled }) => /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px", opacity: disabled ? 0.4 : 1 }, children: [
  /* @__PURE__ */ jsxs("span", { style: { color: "#555", fontWeight: 500 }, children: [
    label,
    ":"
  ] }),
  [true, false].map((v) => /* @__PURE__ */ jsx(
    "button",
    {
      onClick: () => !disabled && onToggle(v),
      disabled,
      style: {
        padding: "4px 14px",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: value === v ? "#2563eb" : "#d1d5db",
        backgroundColor: value === v ? "#eff6ff" : "transparent",
        color: value === v ? "#2563eb" : "#6b7280",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "13px",
        fontWeight: value === v ? 600 : 400
      },
      children: v ? onLabel : offLabel
    },
    String(v)
  ))
] });
const NumericInput = ({ label, value, onChange, min = 1, max = 999, step = 1, width = "72px", disabled }) => /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px", opacity: disabled ? 0.4 : 1 }, children: [
  /* @__PURE__ */ jsxs("span", { style: { color: "#555", fontWeight: 500 }, children: [
    label,
    ":"
  ] }),
  /* @__PURE__ */ jsx(
    "input",
    {
      type: "number",
      value,
      onChange: (e) => {
        const parsed = step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value);
        if (!isNaN(parsed)) onChange(Math.max(min, Math.min(max, parsed)));
      },
      disabled,
      step,
      style: {
        width,
        padding: "4px 8px",
        borderRadius: "6px",
        border: "1px solid #d1d5db",
        fontSize: "13px",
        textAlign: "center",
        cursor: disabled ? "not-allowed" : "text"
      }
    }
  )
] });
const ToggleGroup = ({ label, value, options, onChange, disabled }) => /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px", opacity: disabled ? 0.4 : 1 }, children: [
  /* @__PURE__ */ jsxs("span", { style: { color: "#555", fontWeight: 500 }, children: [
    label,
    ":"
  ] }),
  options.map((opt) => /* @__PURE__ */ jsx(
    "button",
    {
      onClick: () => !disabled && onChange(opt.value),
      disabled,
      style: {
        padding: "4px 14px",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: value === opt.value ? "#2563eb" : "#d1d5db",
        backgroundColor: value === opt.value ? "#eff6ff" : "transparent",
        color: value === opt.value ? "#2563eb" : "#6b7280",
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: "13px",
        fontWeight: value === opt.value ? 600 : 400
      },
      children: opt.label
    },
    opt.value
  ))
] });
const SelectInput = ({ label, value, options, onChange }) => /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [
  /* @__PURE__ */ jsxs("span", { style: { color: "#555", fontWeight: 500 }, children: [
    label,
    ":"
  ] }),
  /* @__PURE__ */ jsx(
    "select",
    {
      value,
      onChange: (e) => onChange(e.target.value),
      style: {
        padding: "4px 8px",
        borderRadius: "6px",
        border: "1px solid #d1d5db",
        fontSize: "13px",
        cursor: "pointer",
        background: "white"
      },
      children: options.map((opt) => /* @__PURE__ */ jsx("option", { value: opt, children: opt }, opt))
    }
  )
] });
const DevOptionsPanel = ({
  isDevMode,
  devOptionsExpanded,
  setDevOptionsExpanded,
  useTextRecall,
  setUseTextRecall,
  pageSize,
  setPageSize,
  rankingWeights,
  handleWeightChange,
  useTargetedSectionTitles,
  setUseTargetedSectionTitles,
  useTwoPhaseFiltering,
  setUseTwoPhaseFiltering,
  useOutlierFiltering,
  setUseOutlierFiltering,
  ignoreRatings,
  setIgnoreRatings,
  ratingThreshold,
  setRatingThreshold,
  llmModel,
  setLlmModel,
  llmModelLight,
  setLlmModelLight,
  searchTemperature,
  setSearchTemperature,
  maxPerBrand,
  setMaxPerBrand,
  maxProductsForLLM,
  setMaxProductsForLLM,
  numSearchAngles,
  setNumSearchAngles,
  targetCategories,
  setTargetCategories,
  semanticThreshold,
  setSemanticThreshold,
  productsPerSection,
  setProductsPerSection,
  productsBuffer,
  setProductsBuffer,
  angleStrategy,
  setAngleStrategy,
  availabilityFilter,
  setAvailabilityFilter,
  useOriginalQueryReranking,
  setUseOriginalQueryReranking,
  recatModel,
  setRecatModel,
  analyzeModel,
  setAnalyzeModel,
  planModel,
  setPlanModel
}) => {
  if (!isDevMode) return null;
  return /* @__PURE__ */ jsxs("div", { style: { borderBottom: "1px solid #e5e7eb", flexShrink: 0 }, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => setDevOptionsExpanded((prev) => !prev),
        style: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "6px 16px",
          background: "#fefce8",
          border: "none",
          cursor: "pointer",
          fontSize: "13px",
          color: "#92400e",
          fontWeight: 500
        },
        children: [
          /* @__PURE__ */ jsx("span", { children: "Dev Options" }),
          /* @__PURE__ */ jsx("span", { style: { fontSize: "12px" }, children: devOptionsExpanded ? "▲" : "▼" })
        ]
      }
    ),
    devOptionsExpanded && /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          padding: "12px 16px",
          fontSize: "13px",
          color: "#555",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          background: "#fffef5"
        },
        children: [
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }, children: [
            /* @__PURE__ */ jsx(TogglePair, { label: "Recall", value: useTextRecall, onLabel: "Text", offLabel: "KNN", onToggle: setUseTextRecall }),
            /* @__PURE__ */ jsx(TogglePair, { label: "Sections", value: useTargetedSectionTitles, onLabel: "Targeted", offLabel: "Legacy", onToggle: setUseTargetedSectionTitles }),
            /* @__PURE__ */ jsx(TogglePair, { label: "KNN", value: useTwoPhaseFiltering, onLabel: "2-Phase", offLabel: "PostFilter", onToggle: setUseTwoPhaseFiltering }),
            /* @__PURE__ */ jsx(TogglePair, { label: "Outlier Filter", value: useOutlierFiltering, onLabel: "On", offLabel: "Off", onToggle: setUseOutlierFiltering }),
            /* @__PURE__ */ jsx(TogglePair, { label: "Ratings", value: ignoreRatings, onLabel: "Ignore", offLabel: "Filter", onToggle: setIgnoreRatings }),
            /* @__PURE__ */ jsx(
              ToggleGroup,
              {
                label: "Search Angles",
                value: angleStrategy,
                options: [
                  { value: "brand", label: "Brand" },
                  { value: "generic", label: "Generic" },
                  { value: "auto", label: "Auto" }
                ],
                onChange: (v) => setAngleStrategy(v)
              }
            ),
            /* @__PURE__ */ jsx(TogglePair, { label: "Rerank", value: useOriginalQueryReranking, onLabel: "On", offLabel: "Off", onToggle: setUseOriginalQueryReranking }),
            /* @__PURE__ */ jsx(
              ToggleGroup,
              {
                label: "Availability",
                value: availabilityFilter,
                options: [
                  { value: "apply", label: "Apply" },
                  { value: "skipFalse", label: "Skip False" },
                  { value: "skipAll", label: "Skip All" }
                ],
                onChange: (v) => setAvailabilityFilter(v)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }, children: [
            /* @__PURE__ */ jsx(SelectInput, { label: "LLM (heavy)", value: llmModel, options: HEAVY_MODELS, onChange: setLlmModel }),
            /* @__PURE__ */ jsx(SelectInput, { label: "LLM (light)", value: llmModelLight, options: LIGHT_MODELS, onChange: setLlmModelLight }),
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [
              /* @__PURE__ */ jsx("span", { style: { color: "#555", fontWeight: 500 }, children: "Recat:" }),
              /* @__PURE__ */ jsx(
                "select",
                {
                  value: recatModel,
                  onChange: (e) => setRecatModel(e.target.value),
                  style: {
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "white"
                  },
                  children: RECAT_MODELS.map((opt) => /* @__PURE__ */ jsx("option", { value: opt, children: opt || `(same as heavy)` }, opt))
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [
              /* @__PURE__ */ jsx("span", { style: { color: "#555", fontWeight: 500 }, children: "Analyze:" }),
              /* @__PURE__ */ jsx(
                "select",
                {
                  value: analyzeModel,
                  onChange: (e) => setAnalyzeModel(e.target.value),
                  style: {
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "white"
                  },
                  children: ANALYZE_PLAN_MODELS.map((opt) => /* @__PURE__ */ jsx("option", { value: opt, children: opt || `(same as light)` }, opt))
                }
              )
            ] }),
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [
              /* @__PURE__ */ jsx("span", { style: { color: "#555", fontWeight: 500 }, children: "Plan:" }),
              /* @__PURE__ */ jsx(
                "select",
                {
                  value: planModel,
                  onChange: (e) => setPlanModel(e.target.value),
                  style: {
                    padding: "4px 8px",
                    borderRadius: "6px",
                    border: "1px solid #d1d5db",
                    fontSize: "13px",
                    cursor: "pointer",
                    background: "white"
                  },
                  children: ANALYZE_PLAN_MODELS.map((opt) => /* @__PURE__ */ jsx("option", { value: opt, children: opt || `(same as light)` }, opt))
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }, children: [
            /* @__PURE__ */ jsx(NumericInput, { label: "Angles", value: numSearchAngles, onChange: setNumSearchAngles, min: 1, max: 12 }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Categories", value: targetCategories, onChange: setTargetCategories, min: 1, max: 10 }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Max/brand", value: maxPerBrand, onChange: setMaxPerBrand, min: 1, max: 20 }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Max LLM", value: maxProductsForLLM, onChange: setMaxProductsForLLM, min: 10, max: 200 }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Page size", value: pageSize, onChange: setPageSize, min: 1, max: 200 })
          ] }),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }, children: [
            /* @__PURE__ */ jsx(NumericInput, { label: "Per section", value: productsPerSection, onChange: setProductsPerSection, min: 1, max: 50 }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Buffer", value: productsBuffer, onChange: setProductsBuffer, min: 1, max: 50, disabled: !useOutlierFiltering }),
            /* @__PURE__ */ jsx(
              NumericInput,
              {
                label: "Rating min",
                value: ratingThreshold,
                onChange: setRatingThreshold,
                min: 0,
                max: 5,
                step: 0.1,
                width: "72px",
                disabled: ignoreRatings
              }
            ),
            /* @__PURE__ */ jsx(NumericInput, { label: "Temp", value: searchTemperature, onChange: setSearchTemperature, min: 0, max: 1, step: 0.1, width: "72px" }),
            /* @__PURE__ */ jsx(NumericInput, { label: "Semantic", value: semanticThreshold, onChange: setSemanticThreshold, min: 0, max: 1, step: 0.05, width: "72px" })
          ] }),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "8px" }, children: [
            /* @__PURE__ */ jsxs("span", { style: { color: "#555", fontWeight: 500 }, children: [
              "Ranking weights",
              " ",
              /* @__PURE__ */ jsxs("span", { style: { fontWeight: 400, color: "#999" }, children: [
                "(total: ",
                Object.values(rankingWeights).reduce((s, v) => s + v, 0).toFixed(2),
                ")"
              ] })
            ] }),
            Object.keys(rankingWeights).map((key) => /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: "10px" }, children: [
              /* @__PURE__ */ jsx("span", { style: { minWidth: "70px", color: "#666", fontSize: "13px" }, children: key.replace("Weight", "") }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  type: "range",
                  min: "0",
                  max: "1",
                  step: "0.01",
                  value: rankingWeights[key],
                  onChange: (e) => handleWeightChange(key, parseFloat(e.target.value)),
                  style: { flex: 1, accentColor: "#2563eb", cursor: "pointer" }
                }
              ),
              /* @__PURE__ */ jsx(
                "span",
                {
                  style: {
                    minWidth: "36px",
                    textAlign: "right",
                    fontSize: "13px",
                    color: "#444",
                    fontVariantNumeric: "tabular-nums"
                  },
                  children: rankingWeights[key].toFixed(2)
                }
              )
            ] }, key))
          ] })
        ]
      }
    )
  ] });
};
export {
  DevOptionsPanel as default
};
//# sourceMappingURL=DevOptionsPanel.js.map
